import { EngineEvaluation, parseEngineInfo } from '@chess-platform/shared';

/**
 * M3: Motor de Análisis Profundo.
 * Utiliza un worker persistente para evitar la latencia de creación de hilos.
 * Maneja una cola de peticiones si es necesario.
 */
class ReviewEngine {
    private worker: Worker | null = null;
    private queue: { fen: string; resolve: (val: EngineEvaluation | null) => void }[] = [];
    private busy = false;

    constructor() {
        this.init();
    }

    private init() {
        if (typeof window === 'undefined') return;
        try {
            this.worker = new Worker('/stockfish.js');
            this.worker.postMessage('uci');
            this.worker.postMessage('setoption name Hash value 32');
            this.worker.postMessage('setoption name Threads value 1');
            this.worker.postMessage('isready');
        } catch (err) {
            console.error('[M3] Error al iniciar worker', err);
        }
    }

    public async reviewPosition(fen: string): Promise<EngineEvaluation | null> {
        return new Promise((resolve) => {
            this.queue.push({ fen, resolve });
            this.processQueue();
        });
    }

    private async processQueue() {
        if (this.busy || this.queue.length === 0) return;

        if (!this.worker) {
            const { resolve } = this.queue.shift()!;
            resolve(null);
            this.processQueue();
            return;
        }

        this.busy = true;

        const { fen, resolve } = this.queue.shift()!;
        let currentEval: EngineEvaluation = { score: null, isMate: false, mateMoves: null, bestMove: null, multipv: [], depth: 0, nps: 0 };
        const turn = fen.split(' ')[1] as 'w' | 'b' || 'w';

        const handler = (e: MessageEvent) => {
            const msg = e.data as string;
            if (msg.startsWith('info depth')) {
                currentEval = parseEngineInfo(msg, turn, currentEval);
            }
            if (msg.startsWith('bestmove')) {
                this.worker!.removeEventListener('message', handler);
                clearTimeout(timeout);
                this.busy = false;
                resolve(currentEval.score !== null ? currentEval : null);
                this.processQueue();
            }
        };

        this.worker.addEventListener('message', handler);
        this.worker.postMessage(`position fen ${fen}`);
        this.worker.postMessage('go depth 13'); // Reduced depth for faster feedback

        const timeout = setTimeout(() => {
            if (this.busy) {
                if (this.worker) this.worker.removeEventListener('message', handler);
                this.busy = false;
                resolve(currentEval.score !== null ? currentEval : null);
                this.processQueue();
            }
        }, 8000);
    }
}

export const reviewEngine = new ReviewEngine();
